/**
 * Eingebettete Editoren (Craft-Wasm, OnlyOffice) im Gleichschritt mit dem Theme-Wechsel.
 *
 * `runThemeTransition` meldet jeden Wechsel direkt nach dem synchronen Schreiben der
 * Variablen an alle angemeldeten Einbettungen — mit der Dauer und Kurve, mit der die Seite
 * selbst ueberblendet. Bei einer View-Transition wartet der Update-Callback auf ihre Antwort
 * (hoechstens `THEMA_ANTWORT_DECKEL_MS`): erst dann hat ein iframe seine Nachricht verarbeitet,
 * und das Live-Bild der Transition zeigt ab dem ersten Frame die neuen Editorfarben.
 *
 * Gemessen 2026-10-23 (Chromium 1223): waehrend eines async Update-Callbacks laeuft im
 * gleich-originigen iframe kein requestAnimationFrame (1 Tick in 120 ms, danach 10). Eine
 * Einbettung darf deshalb nicht auf ihr GEZEICHNETES Bild warten — sie antwortet, sobald der
 * naechste Frame, den sie zeichnet, das Ziel ist. Sonst liefe jeder Wechsel in den Deckel.
 */

export type ThemaUebergang = {
  /** Dauer der Ueberblendung; 0 = sofort aufs Ziel (die View-Transition blendet selbst). */
  ms: number;
  /** CSS-Zeitfunktion (`ease`, `linear`, `cubic-bezier(…)`), dieselbe wie auf der Seite. */
  kurve: string;
};

/** Wird nach dem Schreiben der Variablen gerufen; ein Promise haelt die View-Transition an, bis es erfuellt ist. */
export type ThemaEinbettung = (uebergang: ThemaUebergang) => Promise<void> | void;

export const THEMA_ANTWORT_DECKEL_MS = 150;

export const THEMA_SOFORT: ThemaUebergang = { ms: 0, kurve: "linear" };

const einbettungen = new Set<ThemaEinbettung>();

/** Meldet eine Einbettung an; die Rueckgabe meldet sie wieder ab. */
export function registriereThemaEinbettung(einbettung: ThemaEinbettung): () => void {
  einbettungen.add(einbettung);
  return () => {
    einbettungen.delete(einbettung);
  };
}

/**
 * Ruft alle Einbettungen; `null`, wenn keine etwas zum Warten zurueckgab. Sonst ein Promise,
 * das erfuellt, sobald alle geantwortet haben oder der Deckel abgelaufen ist. Eine Einbettung,
 * die wirft oder ablehnt, haelt den Wechsel nicht auf — er gehoert der Seite, nicht ihr.
 */
export function meldeThemaAnEinbettungen(
  uebergang: ThemaUebergang,
  deckelMs: number = THEMA_ANTWORT_DECKEL_MS,
): Promise<void> | null {
  const warten: Promise<void>[] = [];
  for (const einbettung of Array.from(einbettungen)) {
    try {
      const r = einbettung(uebergang);
      if (r) warten.push(r.catch(() => undefined));
    } catch {
      /* siehe oben: der Wechsel laeuft weiter */
    }
  }
  if (warten.length === 0) return null;
  return new Promise<void>((fertig) => {
    const deckel = setTimeout(fertig, deckelMs);
    void Promise.all(warten).then(() => {
      clearTimeout(deckel);
      fertig();
    });
  });
}

/**
 * Die berechneten Farben der `tokens` AN DER STELLE `wirt` (`--x` → `getComputedStyle`), Alpha
 * unveraendert. Je Token ein frischer Knoten mit `transition:none!important`: unter
 * `data-theme-fx="soft"` uebernimmt jedes Element einen 400-ms-Farbuebergang, und ein
 * wiederverwendeter Knoten lieferte beim Umfaerben Zwischenwerte (eine einfarbige Palette
 * fuer ~0,4 s, gemessen am Craft-Editor 2026-10-23).
 */
export function messePalette(wirt: Element, tokens: readonly string[]): Record<string, string> {
  const farben: Record<string, string> = {};
  for (const token of tokens) {
    const probe = document.createElement("span");
    probe.style.cssText = "position:absolute;width:0;height:0;visibility:hidden;pointer-events:none";
    probe.style.setProperty("transition", "none", "important");
    probe.style.setProperty("background-color", `var(--${token})`);
    wirt.appendChild(probe);
    try {
      farben[token] = getComputedStyle(probe).backgroundColor.trim();
    } finally {
      probe.remove();
    }
  }
  return farben;
}
